import {
  Box,
  Text,
  Avatar,
  Image,
  Checkbox,
} from '@mantine/core';
import {
  IconPhoto,
  IconChevronRight,
} from '@tabler/icons-react';
import type { Asset } from '@/types/models';

interface AssetMobileCardProps {
  asset: Asset;
  onView: (asset: Asset) => void;
  onEdit: (asset: Asset) => void;
  onDelete: (asset: Asset) => void;
  onAssign?: (asset: Asset) => void;
  onUnassign?: (asset: Asset) => void;
  isSelected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
  showSelection?: boolean;
}

export function AssetMobileCard({
  asset,
  onView,
  isSelected = false,
  onSelectionChange,
  showSelection = false,
}: AssetMobileCardProps) {
  // iOS-style: checkbox on left for selection, main area clickable for details
  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid var(--mantine-color-gray-2)',
        backgroundColor: isSelected ? 'var(--mantine-color-blue-0)' : 'transparent',
      }}
    >
      {/* Selection checkbox - always visible when selection is available */}
      {onSelectionChange && (
        <Box
          style={{
            padding: '12px 8px 12px 16px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Checkbox
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelectionChange(e.currentTarget.checked);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </Box>
      )}

      {/* Main content area - clickable to view details */}
      <Box
        onClick={() => onView(asset)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flex: 1,
          padding: onSelectionChange ? '12px 16px 12px 8px' : '12px 16px',
          cursor: 'pointer',
          minWidth: 0,
        }}
      >
        {/* Thumbnail */}
        {asset.photo_url ? (
          <Image
            src={asset.photo_url}
            alt={asset.description}
            w={48}
            h={48}
            radius="sm"
            fit="cover"
            style={{ flexShrink: 0 }}
          />
        ) : (
          <Avatar color="gray" size={48} radius="sm" style={{ flexShrink: 0 }}>
            <IconPhoto size={20} />
          </Avatar>
        )}

        {/* Content - Primary: Description, Secondary: Tag ID */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text fw={500} size="sm" truncate>
            {asset.description || asset.tag_id}
          </Text>
          <Text size="xs" c="dimmed" truncate>
            {asset.tag_id}
          </Text>
        </Box>

        {/* Chevron indicator - always show to indicate tappable */}
        <IconChevronRight size={20} color="var(--mantine-color-gray-5)" style={{ flexShrink: 0 }} />
      </Box>
    </Box>
  );
}
